import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PartnerLink from "@/components/PartnerLink";
import Layout, { StatusDot } from "@/components/Layout";
import { ScoreGauge, StatusBadge, NationBadge } from "@/components/Compliance";
import UploadModal from "@/components/UploadModal";
import TenancyPanel from "@/components/TenancyPanel";
import SupportingDocs from "@/components/SupportingDocs";
import ReadinessCard from "@/components/ReadinessCard";
import HandoverPanel from "@/components/HandoverPanel";
import BookButton from "@/components/BookButton";
import InspectionsRepairs from "@/components/InspectionsRepairs";
import { RentNoticeButton, EpcLookupButton, BulkImportButton } from "@/components/PropertyTools";
import { ShareDialog, DefenceBundleButton } from "@/components/ShareTools";
import PreTenancyWizard from "@/components/PreTenancyWizard";
import { TenantedSalePanel, HandoverChecklist } from "@/components/TenantedSale";
import { NATION_META } from "@/lib/nations";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, FileText, Share2, FileCheck2, Plus, History, ExternalLink, Trash2, Copy, Info } from "lucide-react";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [certs, setCerts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [audit, setAudit] = useState([]);
  const [upload, setUpload] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [notice, setNotice] = useState({ type: "notice", title: "", description: "", date: "" });
  const [tenancies, setTenancies] = useState([]);
  const [docs, setDocs] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    setTick((t) => t + 1);
    api.get("/contractors").then((r) => setContractors(r.data)).catch(() => {});
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => nav("/properties"));
    api.get(`/properties/${id}/certificates`).then((r) => setCerts(r.data)).catch(() => {});
    api.get(`/properties/${id}/notices`).then((r) => setNotices(r.data)).catch(() => {});
    api.get(`/properties/${id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
    api.get(`/properties/${id}/tenancies`).then((r) => setTenancies(r.data)).catch(() => {});
    api.get(`/properties/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    api.get(`/properties/${id}/readiness`).then((r) => setReadiness(r.data)).catch(() => {});
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const markServed = async (c) => {
    const d = window.prompt("Date served to tenant (YYYY-MM-DD)", new Date().toISOString().slice(0, 10));
    if (!d) return;
    try { await api.patch(`/certificates/${c.id}/served`, { served_date: d }); toast.success("Marked as served"); load(); }
    catch { toast.error("Could not update"); }
  };

  const makeShare = async () => {
    const { data } = await api.post(`/properties/${id}/share`);
    const url = `${window.location.origin}/share/${data.token}`;
    setShareUrl(url);
    navigator.clipboard?.writeText(url);
    toast.success("Share link copied to clipboard");
  };

  const addNotice = async () => {
    if (!notice.title) return toast.error("Add a title");
    await api.post(`/properties/${id}/notices`, notice);
    toast.success("Logged");
    setNoticeOpen(false); setNotice({ type: "notice", title: "", description: "", date: "" });
    load();
  };

  const removeProp = async () => {
    if (!window.confirm("Delete this property and all its records?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Property deleted");
    nav("/properties");
  };

  if (!p) return <Layout><div className="py-20 text-center text-muted-foreground">Loading…</div></Layout>;

  return (
    <Layout>
      <Link to="/properties" className="inline-flex items-center gap-2 text-sm text-muted-foreground mb-4 hover:text-foreground"><ArrowLeft size={16} /> All properties</Link>

      <div className="bg-card border border-border rounded-2xl p-6 flex flex-col sm:flex-row gap-6 items-start">
        <ScoreGauge score={p.score} />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2"><NationBadge nation={p.nation} /><StatusBadge status={p.status} /><span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">{p.property_type === "hmo" ? "HMO" : "Single let"}</span></div>
          <h1 className="font-heading text-2xl font-bold">{p.name}</h1>
          <p className="text-muted-foreground">{p.address} {p.postcode}</p>
          <p className="text-xs text-muted-foreground mt-1">{NATION_META[p.nation]?.lawLabel}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="upload-cert-btn" onClick={() => setUpload(true)} className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold transition-colors"><Sparkles size={16} /> Upload certificate</button>
            <Link to={`/properties/${id}/evidence`} data-testid="evidence-btn" className="min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl border border-border font-medium hover:bg-secondary transition-colors"><FileCheck2 size={16} /> Evidence Pack</Link>
            <ShareDialog property={p} />
            <DefenceBundleButton property={p} />
            <RentNoticeButton property={p} />
            <EpcLookupButton property={p} />
            <BulkImportButton property={p} onDone={load} />
            <button data-testid="delete-prop-btn" onClick={removeProp} className="min-h-[44px] px-3 inline-flex items-center gap-2 rounded-xl border border-border text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 size={16} /></button>
          </div>
          {false && <div className="mt-3 flex items-center gap-2 text-xs font-mono bg-secondary rounded-lg px-3 py-2"><span className="truncate">{shareUrl}</span><button onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Copied"); }}><Copy size={14} /></button></div>}
        </div>
      </div>

      <Tabs defaultValue="checklist" className="mt-6">
        <TabsList className="bg-secondary flex-wrap h-auto">
          <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>
          <TabsTrigger value="tenancy" data-testid="tab-tenancy">Tenancy</TabsTrigger>
          <TabsTrigger value="supporting" data-testid="tab-supporting">Supporting docs</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections">Inspections & Repairs</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Certificates</TabsTrigger>
          <TabsTrigger value="notices" data-testid="tab-notices">Notices & Log</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit trail</TabsTrigger>
        </TabsList>

        <TabsContent value="tenancy" className="mt-4 grid lg:grid-cols-2 gap-4 items-start">
          <TenancyPanel property={p} tenancies={tenancies} onChange={load} />
          <div className="space-y-4">
            <HandoverChecklist property={p} />
            <PreTenancyWizard property={p} refreshKey={tick} />
            <ReadinessCard readiness={readiness} />
            <HandoverPanel property={p} refreshKey={tick} />
            <TenantedSalePanel property={p} />
          </div>
        </TabsContent>

        <TabsContent value="supporting" className="mt-4">
          <SupportingDocs property={p} data={docs} onChange={load} />
        </TabsContent>
        <TabsContent value="inspections" className="mt-4">
          <InspectionsRepairs property={p} onChange={load} />
        </TabsContent>

        <TabsContent value="checklist" className="mt-4 space-y-2">
          {p.checklist.map((it) => (
            <div key={it.rule.id} data-testid={`checklist-${it.rule.id}`} className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <StatusDot status={it.status} className="h-3 w-3 mt-1.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{it.rule.name}</span>
                    {it.rule.verify && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">verify source</span>}
                    <span className="text-xs text-muted-foreground">{it.rule.validity_label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.rule.explanation}</p>
                  {it.cert && <p className="text-xs text-muted-foreground mt-1">Issued {it.cert.issue_date || "—"} · Expires {it.cert.expiry_date || "—"} {it.cert.engineer_name ? `· ${it.cert.engineer_name}` : ""}</p>}
                  <a href={it.rule.source_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-brand mt-1 hover:underline">Official source <ExternalLink size={11} /></a>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusBadge status={it.status} label={it.label} />
                  {it.status !== "green" && ["gas", "electrical", "epc", "alarms", "documents", "licence"].includes(it.rule.category) && <BookButton property={p} rule={it.rule} contractors={contractors} onDone={load} />}
                  {it.rule.category === "gas" && <PartnerLink slot="boiler_cover" label="Boiler cover" />}
                </div>
              </div>
            </div>
          ))}
          {p.checklist.length === 0 && <p className="text-sm text-muted-foreground">No rules apply.</p>}
        </TabsContent>

        <TabsContent value="documents" className="mt-4 space-y-2">
          {certs.map((c) => (
            <div key={c.id} data-testid={`doc-${c.id}`} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <FileText className={c.status === "superseded" ? "text-muted-foreground" : "text-emerald-brand"} size={20} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2">{c.cert_type} {c.status === "superseded" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">superseded</span>}</div>
                <div className="text-xs text-muted-foreground">Issued {c.issue_date || "—"} · Expires {c.expiry_date || "—"} · uploaded {new Date(c.uploaded_at).toLocaleDateString()}{c.served_date ? ` · served to tenant ${c.served_date}` : ""}</div>
              </div>
              {c.status === "active" && !c.served_date && <button data-testid={`served-${c.id}`} onClick={() => markServed(c)} className="text-xs px-2 min-h-[32px] rounded-lg border border-border hover:bg-secondary whitespace-nowrap">Mark served</button>}
            </div>
          ))}
          {certs.length === 0 && <p className="text-sm text-muted-foreground">No documents yet. Upload your first certificate.</p>}
        </TabsContent>

        <TabsContent value="notices" className="mt-4">
          <button data-testid="add-notice-btn" onClick={() => setNoticeOpen(true)} className="mb-3 min-h-[44px] px-4 inline-flex items-center gap-2 rounded-xl border border-border font-medium hover:bg-secondary transition-colors"><Plus size={16} /> Log entry</button>
          <div className="space-y-2">
            {notices.map((n) => (
              <div key={n.id} className="bg-card border border-border rounded-2xl p-4">
                <div className="flex items-center gap-2"><span className="text-xs px-2 py-0.5 rounded-full bg-secondary capitalize">{n.type}</span><span className="font-semibold text-sm">{n.title}</span></div>
                {n.description && <p className="text-sm text-muted-foreground mt-1">{n.description}</p>}
                <p className="text-xs text-muted-foreground mt-1">{new Date(n.date).toLocaleString()}</p>
              </div>
            ))}
            {notices.length === 0 && <p className="text-sm text-muted-foreground">No entries logged yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3"><Info size={15} /> Immutable trail — uploads are superseded, never overwritten.</div>
          <div className="space-y-2">
            {audit.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border text-sm">
                <History size={16} className="text-muted-foreground" />
                <div className="flex-1"><span className="font-medium capitalize">{a.action.replace(/_/g, " ")}</span> — {a.detail}{a.actor && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid="audit-actor">by {a.actor}</span>}</div>
                <span className="text-xs text-muted-foreground">{new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
            {audit.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <UploadModal open={upload} onClose={() => setUpload(false)} property={p} onDone={load} />

      <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
        <DialogContent className="bg-card" data-testid="notice-modal">
          <DialogHeader><DialogTitle className="font-heading">Log an entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select data-testid="notice-type" value={notice.type} onChange={(e) => setNotice({ ...notice, type: e.target.value })} className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card">
              {["notice", "inspection", "repair", "communication"].map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
            </select>
            <input data-testid="notice-title" value={notice.title} onChange={(e) => setNotice({ ...notice, title: e.target.value })} placeholder="Title (e.g. Section 21 served)" className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
            <textarea data-testid="notice-desc" value={notice.description} onChange={(e) => setNotice({ ...notice, description: e.target.value })} placeholder="Details" rows={3} className="w-full px-4 py-2 rounded-xl border border-input bg-card" />
            <input data-testid="notice-date" type="datetime-local" value={notice.date} onChange={(e) => setNotice({ ...notice, date: e.target.value })} className="w-full min-h-[44px] px-4 rounded-xl border border-input bg-card" />
            <button data-testid="notice-save" onClick={addNotice} className="w-full min-h-[48px] rounded-xl bg-navy hover:bg-navy-700 text-white font-semibold transition-colors">Save entry</button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
